import { forwardRef, useImperativeHandle, useState } from 'react'
import Turnstile, { useTurnstile } from './Turnstile'
import { CAPTCHA_WIDGET_IDS, type CaptchaWidgetId } from './constants'

interface SentryData {
  mobileNumber?: string
  dialCode?: string
  [key: string]: string | undefined
}

interface CaptchaWidgetProps {
  onVerify: (captchaToken: string, captchaSiteKey: string) => void
  onError?: (errorCode: string, captchaSiteKey: string) => void
  widgetId?: CaptchaWidgetId
  style?: React.CSSProperties
  sentryData?: SentryData
  size?: 'normal' | 'compact' | 'flexible' | 'invisible'
}

export interface CaptchaWidgetRef {
  reset: () => void
}

const CaptchaWidget = forwardRef<CaptchaWidgetRef, CaptchaWidgetProps>(
  (
    { onVerify, onError, widgetId = CAPTCHA_WIDGET_IDS.DEFAULT, style, sentryData, size },
    ref
  ) => {
    const [showFallbackCaptcha, setShowFallbackCaptcha] = useState(false)
    const captcha = useTurnstile()

    const captchaSiteKey = process.env.REACT_APP_CLOUDFLARE_CAPTCHA_SITE_KEY || ''
    const fallbackCaptchaSiteKey =
      process.env.REACT_APP_CLOUDFLARE_CAPTCHA_SITE_KEY_FALLBACK || ''

    const currentWidgetId = showFallbackCaptcha ? `${widgetId}_fallback` : widgetId
    const currentSiteKey = showFallbackCaptcha
      ? fallbackCaptchaSiteKey
      : captchaSiteKey

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          captcha.reset(currentWidgetId)
        },
      }),
      [captcha, currentWidgetId]
    )

    const logCaptchaError = (message: string, data?: Record<string, any>) => {
      // eslint-disable-next-line no-console
      console.error(`[captcha] ${message}`, {
        ...data,
        siteKey: currentSiteKey,
        widgetContainerId: currentWidgetId,
        isFallback: showFallbackCaptcha,
        mobileNumber: sentryData?.mobileNumber
          ? sentryData.mobileNumber.slice(-4)
          : undefined,
      })
    }

    // Switch to fallback captcha when the default one fails.
    // Doc: https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/error-codes
    const handleCaptchaError = (errorCode: string) => {
      logCaptchaError('Turnstile captcha error', { errorCode })

      if (!showFallbackCaptcha) {
        setShowFallbackCaptcha(true)
      }

      if (onError) {
        onError(errorCode, currentSiteKey)
      }
    }

    const handleVerify = (captchaToken: string) => {
      onVerify(captchaToken, currentSiteKey)
    }

    return (
      <Turnstile
        key={currentWidgetId}
        id={currentWidgetId}
        style={style}
        sitekey={currentSiteKey}
        size={size}
        onVerify={handleVerify}
        onError={handleCaptchaError}
      />
    )
  }
)

CaptchaWidget.displayName = 'CaptchaWidget'

export default CaptchaWidget
